/*
 *   File : order.js
 *   Author : https://evoqins.com
 *   Description : Order list tab file
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap/dist/js/bootstrap.min';

// custom components
import { CustomDataTable } from '../../../Components/DataTable';
import { PrimaryButton, SecondaryButton, TextButton } from '../../../Components/Buttons';
import { CustomDatePicker, CustomSearchInput, CustomSelectBox, DateRangePicker } from '../../../Components/FormElements';
import { Chip, CustomTab } from '../../../Components/Others';
import { Icon } from '../../../Components/Icon';
import EmptyScreen from '../../../Components/Others/empty-screen';
import { OtherTransactionDetailModal, SystematicPlanOrderModal } from '../../../Components/Modal';
import { APIService } from '../../../Service';

// custom style
import color from '../../../Style/color.scss';
import { filterOrderTypeOptions, ORDER_TYPE_BY_TAB, getActiveTransactionTab, getOtherTransactionsListColumns } from '../../OtherTransactions/transaction-table-config';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';

const TAB_TITLES = [
    { id: 1, label: 'All' },
    { id: 2, label: 'Goals' }, //1
    { id: 6, label: 'Insta Save' },//5
    { id: 4, label: 'Smart Baskets' },//3
    { id: 5, label: 'Gold Maxx' },//4
    { id: 3, label: 'Self Managed' }, //2
    { id: 7, label: 'Switch' },
    { id: 9, label: 'STP' },
    { id: 8, label: 'SWP' },
];

const OTHER_TRANSACTION_TAB_MAP = {
    7: 1,
    8: 2,
    9: 3,
};

const isOtherTransactionTab = (tabId) => Object.prototype.hasOwnProperty.call(OTHER_TRANSACTION_TAB_MAP, tabId);

const renameIdsToValues = (list = []) => (
    list.map((item) => {
        if (item && Object.prototype.hasOwnProperty.call(item, 'id')) {
            const { id, ...rest } = item;
            return {
                ...rest,
                value: id,
            };
        }
        return item;
    })
);

const resolveSchemeOption = (options = [], id, label) => {
    if (id != null) {
        const byId = options.find((item) => String(item.value) === String(id));
        if (byId) return byId;
    }

    if (label) {
        const normalized = String(label).trim().toLowerCase();
        const byLabel = options.find((item) => String(item.label || "").trim().toLowerCase() === normalized);
        if (byLabel) return byLabel;
    }

    if (id != null || label) {
        return { value: id ?? null, label: label || "-" };
    }

    return null;
};

const shouldShowFailureReason = (row) => {
    if (!row?.failure_reason) {
        return false;
    }

    const statuses = [
        row.order_status,
        row.fp_order_status,
        row.payment_status,
        row.fp_payment_status,
    ];

    return statuses.some((status) => status === 'Failed');
};

const Order = () => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);
    const hasOrderDownloadAccess = PROFILE_DATA?.modules?.some(
        (module) => module?.name?.toLowerCase() === "order download"
    );

    const { state } = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isRenderedFilter = useRef(true);
    const previousTab = useRef(null);
    const scrollView = useRef(null);

    const clearRef = useRef(false);

    const [searchValue, setSearchValue] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState(-1);

    const [toggleFilter, setToggleFilter] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
    const [loader, setLoader] = useState(true);
    const [searched, setSearched] = useState(false);

    const [userOrderData, setUserOrderData] = useState({});

    const [orderFilterData, setOrderFilterData] = useState({});
    const [filterLoader, setFilterLoader] = useState(true);

    const [checked, setChecked] = useState([]);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [rayiPaymentStatus, setRayiPaymentStatus] = useState(null);
    const [paymentType, setPaymentType] = useState(null);

    const [orderDate, setOrderDate] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [selectedFromScheme, setSelectedFromScheme] = useState(null);
    const [selectedToScheme, setSelectedToScheme] = useState(null);

    useEffect(() => {
        if (isRenderedFilter.current === true) {
            _filterOrder();
        }
        isRenderedFilter.current = false
    }, [])

    useEffect(() => {
        if (selectedTab !== null && previousTab.current !== null && previousTab.current !== selectedTab) {
            const waitingForSchemeId = (selectedTab === 8 && state?.scheme && state?.mf_id == null)
                || (selectedTab === 9 && (state?.from_scheme || state?.to_scheme) && state?.mf_id == null && state?.target_mf_id == null);
            if (!waitingForSchemeId) {
                _detailOrder();
            }
        }
        previousTab.current = selectedTab;
    }, [selectedTab]);

    useEffect(() => {
        if (clearRef.current === true) {
            _detailOrder();
            clearRef.current = false;
        }
    }, [clearRef.current]);


    useEffect(() => {
        if (state !== null) {
            if (state.order_tab) {
                setSelectedTab(state.order_tab);
            } else if (state.other_transactions_tab) {
                const transactionTab = getActiveTransactionTab(state.other_transactions_tab);
                const legacyOrderTabMap = { 1: 7, 2: 8, 3: 9 };
                setSelectedTab(legacyOrderTabMap[transactionTab] || 7);
            } else {
                setSelectedTab(1);
            }

            if (state.mf_id != null || state.scheme || state.target_mf_id || state.from_scheme || state.to_scheme) {
                setToggleFilter(true);
                if (state.order_tab === 8 && (state.mf_id != null || state.scheme)) {
                    setSelectedScheme({ value: state.mf_id ?? null, label: state.scheme || "-" });
                }
                if (state.order_tab === 9) {
                    if (state.mf_id != null || state.from_scheme) {
                        setSelectedFromScheme({ value: state.mf_id ?? null, label: state.from_scheme || "-" });
                    }
                    if (state.target_mf_id != null || state.to_scheme) {
                        setSelectedToScheme({ value: state.target_mf_id ?? null, label: state.to_scheme || "-" });
                    }
                }
            }
        } else {
            setSelectedTab(1);
        }

    }, [state]);

    useEffect(() => {
        if (selectedTransaction) {
            const modalId = selectedTab === 8 || selectedTab === 9 ? 'systematic-plan-order-modal' : 'other-transaction-detail-modal';
            const detailModal = new Modal(document.getElementById(modalId));
            detailModal.show();
        }
    }, [selectedTransaction]);

    // handle tab change
    const _handleTab = (tab_id) => {
        if (TAB_TITLES.find((tab) => tab.id === tab_id)?.disabled) {
            return;
        }
        setCurrentPage(1)
        setLoader(true)
        navigate(location.pathname.includes("support") ? `/support/${id}` : `/customers/${id}`, {
            state: {
                ...state,
                order_tab: tab_id,
                inner_tab_index: state?.inner_tab_index || 2,
                mf_id: undefined,
                scheme: undefined,
                from_scheme: undefined,
                target_mf_id: undefined,
                to_scheme: undefined,
            }
        });

        setSelectedOrderStatus(orderFilterData.order_status?.[0] || null);
        if (!isOtherTransactionTab(tab_id)) {
            setSelectedType(orderFilterData.order_type?.[0] || null);
            setPaymentType(orderFilterData.payment_type?.[0] || null);
        }
        setRayiPaymentStatus(orderFilterData.payment_status?.[0] || null);
        setOrderDate(null);
        setSelectedScheme(orderFilterData.scheme?.[0] || null);
        setSelectedFromScheme(orderFilterData.scheme?.[0] || null);
        setSelectedToScheme(orderFilterData.scheme?.[0] || null);

        setToggleFilter(false);
        setSearchValue("");
    };

    // handle download list 
    const _downloadList = () => {
        // if (checked.length > 0) {  // Ensure checked array has at least one element
        _downloadOrder();
        // } else {
        // toast.dismiss();
        // toast.error('Please select at least one order to download.', { type: "error" });
        // }
    };


    // handle table page change 
    const _handleTablePageChange = (page_num) => {

        setCurrentPage(page_num);
        _detailOrder(page_num)
        if (scrollView.current) {
            scrollView.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setLoader(true);
    };

    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter(prev => !prev);
    };

    // handle select filter 
    const _handleSelectFilter = (selected, type) => {
        setCurrentPage(1);
        if (type === 1) {
            setRayiPaymentStatus(selected);
        } else if (type === 2) {
            setSelectedOrderStatus(selected);
        } else if (type === 3) {
            setSelectedType(selected);
        } else {
            setPaymentType(selected);
        }
    };


    // handle reset filter 
    const _handleClear = () => {
        setSelectedOrderStatus(orderFilterData.order_status?.[0] || null);
        setSelectedType(orderFilterData.order_type?.[0] || null);
        setRayiPaymentStatus(orderFilterData.payment_status?.[0] || null);
        setPaymentType(orderFilterData.payment_type?.[0] || null);
        setOrderDate(null);
        setSelectedScheme(orderFilterData.scheme?.[0] || null);
        setSelectedFromScheme(orderFilterData.scheme?.[0] || null);
        setSelectedToScheme(orderFilterData.scheme?.[0] || null);

        if (state?.mf_id != null || state?.scheme || state?.target_mf_id || state?.from_scheme || state?.to_scheme) {
            navigate(location.pathname.includes("support") ? `/support/${id}` : `/customers/${id}`, {
                state: {
                    ...state,
                    mf_id: undefined,
                    scheme: undefined,
                    from_scheme: undefined,
                    target_mf_id: undefined,
                    to_scheme: undefined,
                }
            });
        }

        clearRef.current = true;
        setLoader(true);
    };

    // handle filter
    const _handleFilter = () => {
        setLoader(true);
        _detailOrder();
    };

    // handle search entered
    const _onSearch = () => {
        setSearched(true);
        setLoader(true);
        _detailOrder();
    };

    // handle search cleared
    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            _detailOrder(currentPage, "");
        }
    };

    // handle search value changed
    const _handleChange = (e) => {
        setSearchValue(e)
        if (e.length === 0) {
            _handleSearchClear();
        }
    };


    // API -user detail
    const _detailOrder = (page_num = currentPage, search = searchValue, schemeIds = {}) => {

        let updated_payment_status = [];
        let updated_order_status = [];
        let updated_type = [];

        if (rayiPaymentStatus !== null && rayiPaymentStatus?.value !== null) {
            updated_payment_status = rayiPaymentStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedOrderStatus !== null && selectedOrderStatus?.value !== null) {
            updated_order_status = selectedOrderStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedType !== null && selectedType?.value !== null) {
            updated_type = selectedType.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        const order_start_date = orderDate === null ? null : format(new Date(orderDate[0]), 'dd-MM-yyyy');
        const order_end_date = orderDate === null ? null : format(new Date(orderDate[1]), 'dd-MM-yyyy');
        const activeTab = schemeIds.orderTab ?? selectedTab;
        const transactionTab = OTHER_TRANSACTION_TAB_MAP[activeTab];

        let is_post = true;
        let url = 'user/list-order';
        let request = isOtherTransactionTab(activeTab) ? {
            "query": search,
            "customer_id": id,
            "page_num": page_num,
            "page_size": 10,
            "investment_type": null,
            "type": [ORDER_TYPE_BY_TAB[transactionTab]],
            "order_status": updated_order_status,
            "payment_status": updated_payment_status,
            "order_start_date": order_start_date,
            "order_end_date": order_end_date,
            "mf_id": transactionTab === 2 ? (schemeIds.mf_id !== undefined ? schemeIds.mf_id : (selectedScheme?.value != null ? selectedScheme.value : (state?.mf_id ?? null)))
                : transactionTab === 3 ? (schemeIds.mf_id !== undefined ? schemeIds.mf_id : (selectedFromScheme?.value != null ? selectedFromScheme.value : (state?.mf_id ?? null)))
                    : null,
            "target_mf_id": transactionTab === 3 ? (schemeIds.target_mf_id !== undefined ? schemeIds.target_mf_id : (selectedToScheme?.value != null ? selectedToScheme.value : (state?.target_mf_id ?? null))) : null
        } : {
            "query": search,
            "customer_id": id,
            "page_num": page_num,
            "page_size": 10,
            "investment_type": selectedTab === 1 ? null : selectedTab - 1, //1-Goal, 2-Basket, 3- Self Managed
            "type": updated_type,
            "order_status": updated_order_status,
            "payment_type": paymentType?.value,
            "payment_status": updated_payment_status,

            "order_start_date": order_start_date,
            "order_end_date": order_end_date
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // setUserOrderData(response.data);

                const updated_data = response.data.orders.map((item, index) => ({
                    ...item,
                    _index: index
                }));

                setUserOrderData({
                    ...response.data,
                    orders: updated_data
                });
            } else {
                setUserOrderData({ orders: [] });
            }
            setLoader(false);
        });
    };

    // API - user detail download
    const _downloadOrder = () => {

        let updated_payment_status = [];
        let updated_order_status = [];
        let updated_type = [];

        if (rayiPaymentStatus !== null && rayiPaymentStatus?.value !== null) {
            updated_payment_status = rayiPaymentStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedOrderStatus !== null && selectedOrderStatus?.value !== null) {
            updated_order_status = selectedOrderStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedType !== null && selectedType?.value !== null) {
            updated_type = selectedType.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        const order_start_date = orderDate === null ? null : format(new Date(orderDate[0]), 'dd-MM-yyyy');
        const order_end_date = orderDate === null ? null : format(new Date(orderDate[1]), 'dd-MM-yyyy');
        const transactionTab = OTHER_TRANSACTION_TAB_MAP[selectedTab];

        let is_post = true;
        let url = 'user/order-download';
        let request = isOtherTransactionTab(selectedTab) ? {
            "query": searchValue === "" ? null : searchValue,
            "customer_id": id,
            "investment_type": null,
            "type": [ORDER_TYPE_BY_TAB[transactionTab]],
            "order_status": updated_order_status,
            "payment_status": updated_payment_status,
            "order_start_date": order_start_date,
            "order_end_date": order_end_date,
            "mf_id": transactionTab === 2 ? (selectedScheme?.value != null ? selectedScheme.value : (state?.mf_id ?? null))
                : transactionTab === 3 ? (selectedFromScheme?.value != null ? selectedFromScheme.value : (state?.mf_id ?? null))
                    : null,
            "target_mf_id": transactionTab === 3 ? (selectedToScheme?.value != null ? selectedToScheme.value : (state?.target_mf_id ?? null)) : null
        } : {
            "query": searchValue === "" ? null : searchValue,
            "customer_id": id,
            "investment_type": selectedTab === 1 ? null : selectedTab - 1, //1-Goal, 2-Basket, 3- Self Managed
            "type": updated_type,
            "order_status": updated_order_status,
            "payment_type": paymentType?.value,
            "payment_status": updated_payment_status,

            "order_start_date": order_start_date,
            "order_end_date": order_end_date
        }
        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // window.open(response.data.url, '_blank');
                // const url = response.data.url;
                // const link = document.createElement('a');
                // link.href = url;
                // link.download = ''; // Optional: specify a file name
                // document.body.appendChild(link);
                // link.click();
                // document.body.removeChild(link);
                // setChecked([]);

                toast.dismiss();
                toast.success("The document has been sent to your email.", {
                    type: "success",
                });

                setDownloadLoader(false);

            } else {
                setDownloadLoader(false);

                toast.dismiss();
                toast.success(response.message, { type: "error" })
            }
            setDownloadLoader(false);
        })
    };

    // API - user detail filter
    const _filterOrder = () => {
        let is_post = false;
        let url = 'orders/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                const filteredOrderTypes = filterOrderTypeOptions(response.data.order_type);
                const schemeOptions = renameIdsToValues(response.data.scheme || []);
                setOrderFilterData({
                    ...response.data,
                    order_type: filteredOrderTypes,
                    scheme: schemeOptions,
                });

                setSelectedOrderStatus(response.data.order_status[0]);
                setSelectedType(filteredOrderTypes[0]);
                setRayiPaymentStatus(response.data.payment_status[0]);
                setPaymentType(response.data.payment_type[0]);

                const incomingSwp = state?.order_tab === 8 && (state?.mf_id != null || state?.scheme);
                const incomingStp = state?.order_tab === 9 && (state?.mf_id != null || state?.from_scheme || state?.target_mf_id || state?.to_scheme);
                const nextScheme = incomingSwp
                    ? (resolveSchemeOption(schemeOptions, state?.mf_id, state?.scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);
                const nextFromScheme = incomingStp
                    ? (resolveSchemeOption(schemeOptions, state?.mf_id, state?.from_scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);
                const nextToScheme = incomingStp
                    ? (resolveSchemeOption(schemeOptions, state?.target_mf_id, state?.to_scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);

                setSelectedScheme(nextScheme);
                setSelectedFromScheme(nextFromScheme);
                setSelectedToScheme(nextToScheme);

                if (incomingSwp || incomingStp) {
                    _detailOrder(1, searchValue, {
                        orderTab: state.order_tab,
                        mf_id: incomingSwp ? (nextScheme?.value ?? null) : (nextFromScheme?.value ?? null),
                        target_mf_id: incomingStp ? (nextToScheme?.value ?? null) : null,
                    });
                }
            }
            else {
                setOrderFilterData([]);
            }
            setFilterLoader(false);
        });
    };

    const _expandableRows = ({ data }) => {

        const bgClass = data._index % 2 === 0 ? 'e-bg-magnolia-dark' : 'e-bg-magnolia-extra-dark';

        if (!data.data || data.data.length === 0) {
            return <div className={`${bgClass} p-3 e-text-black e-primary-font e-font-14 e-font-weight-400 line-height-16px`}>No schemes available.</div>;
        }

        return (
            <div className={`py-3 padding-30px-lr d-flex flex-column gap-12px ${bgClass} `} >
                {data.data.map((child_data, index) => (
                    <div key={index} className='d-flex align-items-center '>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "180px" }} data-column-id="orders_order_date">
                            {child_data.order_date || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "160px" }} data-column-id="orders_type" title={child_data.type || undefined}>
                            {child_data.type || "-"}
                        </p>
                        <div style={{ width: "100px" }} data-column-id="orders_order_type">
                            <Chip label={"Child"}
                                textAlign="justify-content-start"
                                className={"bg-transparent e-text-green-crayola e-border-1px-green-crayola "} />
                        </div>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "140px" }} data-column-id="orders_product">
                            {child_data.product || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px pe-1 text-truncate' style={{ width: "240px" }} data-column-id="orders_product_name" title={child_data.product_name || undefined}>
                            {child_data.product_name || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-20px pe-3 text-truncate' style={{ width: "300px" }} data-column-id="orders_scheme" title={child_data.scheme_name || undefined}>
                            {child_data.scheme_name}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "150px" }} data-column-id="orders_order_amount">
                            {child_data.amount_text || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "150px" }} data-column-id="orders_invested_amount">
                            {child_data.purchase_amount_text || "-"}
                        </p>
                        <div style={{ width: "150px" }} data-column-id="orders_payment_status">
                            {child_data.payment_status !== null ?
                                <Chip label={child_data.payment_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.payment_status === "Success" || child_data.payment_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.payment_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.payment_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                :
                                "-"
                            }
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_rayi_order_status">
                            {child_data.order_status !== null ?
                                <Chip label={child_data.order_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.order_status === "Success" || child_data.order_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.order_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.order_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                :
                                "-"}
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_fp_payment_status">
                            {child_data.fp_payment_status !== null ?
                                <Chip label={child_data.fp_payment_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.fp_payment_status === "Success" || child_data.fp_payment_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.fp_payment_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.fp_payment_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                : "-"}
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_fp_order_status">
                            {child_data.fp_order_status !== null ?
                                <Chip label={child_data.fp_order_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.fp_order_status === "Success" || child_data.fp_order_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.fp_order_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.fp_order_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                : "-"}
                        </div>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "280px" }} data-column-id="orders_failure_reason" title={shouldShowFailureReason(child_data) ? (child_data.failure_reason || undefined) : undefined}>
                            {shouldShowFailureReason(child_data) ? child_data.failure_reason : "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_payment_type">
                            {child_data.payment_type || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "140px" }} data-column-id="orders_allocated_units">
                            {child_data.allocated_units !== null ? child_data.allocated_units : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "140px" }} data-column-id="orders_allocated_nav">
                            {child_data.allocated_nav !== null ? child_data.allocated_nav : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_nav_date">
                            {child_data.nav_date || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "160px" }} data-column-id="orders_folio" title={child_data.folio_no != null ? String(child_data.folio_no) : undefined}>
                            {child_data.folio_no != null ? child_data.folio_no : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "320px" }} data-column-id="orders_fp_order_id" title={child_data.fp_order_id || undefined}>
                            {child_data.fp_order_id || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "130px" }} data-column-id="orders_order_id" title={child_data.order_display_id || undefined}>
                            {child_data.order_display_id || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_allocated_date">
                            {child_data.allocated_date !== null ? child_data.allocated_date : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "180px" }} data-column-id="orders_status_updated_on">
                            {child_data.order_status_updated_on || "-"}
                        </p>
                    </div>
                ))}
            </div>
        );
    };

    // Order data table column 
    const ALL_DATA = [
        {
            name: 'Order Date',
            selector: row => row.order_date,
            sortable: true,
            format: row => row.order_date,
            width: "180px",
            id: "orders_order_date",
        },
        {
            name: 'Type',
            selector: row => row.type,
            cell: row => <span className='text-truncate d-block' title={row.type || undefined}>{row.type || "-"}</span>,
            width: "160px",
            id: "orders_type",
        },
        {
            name: 'Order Type',
            cell: row => <Chip label={"Parent"}
                className={"e-bg-green-crayola"} />,
            width: "100px",
            id: "orders_order_type",
        },
        {
            name: 'Product',
            selector: row => row.product,
            width: "140px",
            id: "orders_product",
        },
        {
            name: 'Product Name',
            cell: row => <div className="text-truncate" title={row.product_name || undefined}>{row.product_name || "-"}</div>,
            width: "240px",
            id: "orders_product_name",
        },
        {
            name: 'Scheme',
            cell: row => <div className="text-truncate" title={row.scheme_name || undefined}>
                {row.scheme_name !== null ? row.scheme_name : "-"}
            </div>,
            width: "300px",
            id: "orders_scheme",
        },
        {
            name: 'Order Amount',
            selector: row => row.amount_text || "-",
            width: "150px",
            id: "orders_order_amount",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.amount < b.amount) return -1;
                if (a.amount > b.amount) return 1;
                return 0;
            }
        },
        {
            name: 'Invested Amount',
            selector: row => row.purchase_amount_text,
            width: "150px",
            id: "orders_invested_amount",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.purchase_amount < b.purchase_amount) return -1;
                if (a.purchase_amount > b.purchase_amount) return 1;
                return 0;
            }
        },

        {
            name: 'Rayi Payment Status',
            cell: row => <>
                {row.payment_status !== null ?
                    <Chip label={row.payment_status}
                        className={row.payment_status === "Success" || row.payment_status == "Successful" ? "e-bg-green-crayola" : row.payment_status === "Failed" ? "e-bg-maximum-red" : row.payment_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "150px",
            id: "orders_payment_status",
        },
        {
            name: <span className="e-table-th-wrap">Rayi Order<br />Status</span>,
            cell: row => <>
                {row.order_status !== null ?
                    <Chip label={row.order_status}
                        className={row.order_status === "Success" || row.order_status == "Successful" ? "e-bg-green-crayola" : row.order_status === "Failed" ? "e-bg-maximum-red" : row.order_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    : "-"}
            </>,
            width: "160px",
            id: "orders_rayi_order_status",
        },
        {
            name: 'FP Payment Status',
            cell: row => <>
                {row.fp_payment_status ?
                    <Chip label={row.fp_payment_status}
                        className={row.fp_payment_status === "Success" || row.fp_payment_status == "Successful" ? "e-bg-green-crayola" : row.fp_payment_status === "Failed" ? "e-bg-maximum-red" : row.fp_payment_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "150px",
            id: "orders_fp_payment_status",
        },
        {
            name: <span className="e-table-th-wrap">FP Order<br />Status</span>,
            cell: row => <>
                {row.fp_order_status ?
                    <Chip label={row.fp_order_status}
                        className={row.fp_order_status === "Success" || row.fp_order_status == "Successful" ? "e-bg-green-crayola" : row.fp_order_status === "Failed" ? "e-bg-maximum-red" : row.fp_order_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "160px",
            id: "orders_fp_order_status",
        },
        {
            name: 'Failed Reason',
            cell: row => <div className="text-truncate" title={shouldShowFailureReason(row) ? (row.failure_reason || undefined) : undefined}>{shouldShowFailureReason(row) ? row.failure_reason : "-"}</div>,
            width: "280px",
            id: "orders_failure_reason",
        },
        {
            name: 'Payment Type',
            selector: row => row.payment_type || "-",
            width: "130px",
            id: "orders_payment_type",
        },
        {
            name: 'Allocated Units',
            selector: row => row.allocated_units !== null ? row.allocated_units : "-",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.allocated_units < b.allocated_units) return -1;
                if (a.allocated_units > b.allocated_units) return 1;
                return 0;
            },
            width: "140px",
            id: "orders_allocated_units",
        },
        {
            name: 'Allocated NAV',
            selector: row => row.allocated_nav !== null ? row.allocated_nav : "-",
            width: "140px",
            id: "orders_allocated_nav",
        },
        {
            name: 'NAV Date',
            selector: row => row.nav_date !== null ? row.nav_date : "-",
            width: "130px",
            id: "orders_nav_date",
        },
        {
            name: 'Folio',
            selector: row => row.folio_no || "-",
            cell: row => <span className="text-truncate d-block" title={row.folio_no || undefined}>{row.folio_no || "-"}</span>,
            width: "160px",
            id: "orders_folio",
        },
        {
            name: "FP Order ID",
            selector: row => row.fp_order_id ? row.fp_order_id : "-",
            cell: row => <span className="text-truncate d-block" title={row.fp_order_id || undefined}>{row.fp_order_id ? row.fp_order_id : "-"}</span>,
            width: "320px",
            id: "orders_fp_order_id",
        },
        {
            name: 'Order ID',
            selector: row => row.order_display_id || "-",
            cell: row => <span className="text-truncate d-block" title={row.order_display_id || undefined}>{row.order_display_id || "-"}</span>,
            width: "130px",
            id: "orders_order_id",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.order_id < b.order_id) return -1;
                if (a.order_id > b.order_id) return 1;
                return 0;
            }
        },
        {
            name: 'Allocated Date',
            selector: row => row.allocated_date || "-",
            cell: row => <>{row.allocated_date || "-"}</>,
            sortable: true,
            format: row => row.allocated_date,
            width: "130px",
            id: "orders_allocated_date",
        },
        {
            name: 'Order status updated on',
            selector: row => row.order_status_updated_on || "-",
            sortable: true,
            width: "180px",
            id: "orders_status_updated_on",
        },

    ];

    const isTransactionView = isOtherTransactionTab(selectedTab);
    const tableColumns = isTransactionView
        ? getOtherTransactionsListColumns({
            includeCustomerColumns: false,
            onViewDetails: setSelectedTransaction,
            tabId: OTHER_TRANSACTION_TAB_MAP[selectedTab],
        })
        : ALL_DATA;
    const expandableRowsComponent = isTransactionView ? null : _expandableRows;

    return (
        <div ref={scrollView} className='padding-32px-top '>
            <div className='padding-32px-lr'>
                {/* Custom tab */}
                <div className='d-flex'>
                    <CustomTab data={TAB_TITLES}
                        selectedTab={selectedTab}
                        tooltip={false}
                        handleClick={_handleTab} />
                </div>

                <div className='pt-4 d-flex align-items-center justify-content-end gap-16px pb-3'>
                    {hasOrderDownloadAccess &&
                        <SecondaryButton name="Download List"
                            disabled={loader || userOrderData?.orders?.length === 0 ? true : false}
                            className={downloadLoader ? "w-129px h-42px" : ""}
                            loader={downloadLoader}
                            onClick={_downloadList} />
                    }

                    {/* Search input */}
                    < CustomSearchInput placeholder="Search with order ID or FP Order ID"
                        type="text"
                        id="input-field"
                        value={searchValue}
                        onClear={_handleSearchClear}
                        className="w-340px "
                        onKeyPress={() => _onSearch()}
                        onSearchClear={() => _handleSearchClear()}
                        onSearch={() => _onSearch()}
                        handleChange={_handleChange} />

                    {/* Filter button */}
                    <div className={`${toggleFilter ? 'e-bg-blue-violet' : ''} padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                        onClick={_handleToggleFilter} >
                        <Icon icon='filter'
                            size='1.25rem'
                            className="d-flex"
                            color={toggleFilter && color.white} />
                    </div>
                </div>
            </div>

            {/* Filter section */}
            <div className={`e-filter-content ${toggleFilter && filterLoader === false ? 'expanded pt-3 pb-4' : 'collapsed'}  e-bg-main-white  padding-32px-lr`}>
                <div className='d-flex justify-content-end'>
                    <TextButton name="Clear all"
                        onClick={_handleClear} />
                </div>

                <div className='d-flex gap-3 padding-32px-bottom flex-wrap'>

                    {selectedTab === 8 &&
                        <CustomSelectBox title='Scheme'
                            placeholder='Select'
                            controlWidth='16rem'
                            loader={filterLoader}
                            options={orderFilterData.scheme}
                            isSearchable={true}
                            value={selectedScheme}
                            onChange={(data) => {
                                setSelectedScheme(data);
                                setCurrentPage(1);
                            }} />
                    }

                    {selectedTab === 9 &&
                        <>
                            <CustomSelectBox title='From Scheme'
                                placeholder='Select'
                                controlWidth='16rem'
                                loader={filterLoader}
                                options={orderFilterData.scheme}
                                isSearchable={true}
                                value={selectedFromScheme}
                                onChange={(data) => {
                                    setSelectedFromScheme(data);
                                    setCurrentPage(1);
                                }} />

                            <CustomSelectBox title='To Scheme'
                                placeholder='Select'
                                controlWidth='16rem'
                                loader={filterLoader}
                                options={orderFilterData.scheme}
                                isSearchable={true}
                                value={selectedToScheme}
                                onChange={(data) => {
                                    setSelectedToScheme(data);
                                    setCurrentPage(1);
                                }} />
                        </>
                    }

                    {(selectedTab !== 8 && selectedTab !== 9) &&
                        <DateRangePicker label="Order date"
                            placeholder="Select"
                            value={orderDate}
                            handleClear={() => setOrderDate(null)}
                            handleChange={(date) => setOrderDate(date)} />
                    }

                    {/* Hidden for Switch / SWP / STP — no payment status for these flows */}
                    {!isTransactionView &&
                        <CustomSelectBox title='Rayi Payment Status'
                            placeholder='Select'
                            controlWidth='10rem'
                            options={orderFilterData.payment_status}
                            isSearchable={false}
                            isMulti={true}
                            type={3}
                            value={rayiPaymentStatus}
                            onChange={(data) => _handleSelectFilter(data, 1)} />
                    }

                    {/* Order status select */}
                    <CustomSelectBox title='Rayi Order Status'
                        placeholder='Select'
                        controlWidth='10rem'
                        options={orderFilterData.order_status}
                        isSearchable={false}
                        value={selectedOrderStatus}
                        isMulti={true}
                        type={3}
                        onChange={(data) => _handleSelectFilter(data, 2)} />

                    {(selectedTab === 8 || selectedTab === 9) &&
                        <DateRangePicker label="Order date"
                            placeholder="Select"
                            value={orderDate}
                            handleClear={() => setOrderDate(null)}
                            handleChange={(date) => setOrderDate(date)} />
                    }

                    {!isTransactionView &&
                        <>
                            {/* Type select */}
                            <CustomSelectBox title='Type'
                                placeholder='Select'
                                controlWidth='10rem'
                                options={orderFilterData.order_type}
                                isSearchable={false}
                                value={selectedType}
                                isMulti={true}
                                type={3}
                                onChange={(data) => _handleSelectFilter(data, 3)} />

                            {/* Payment status select */}
                            <CustomSelectBox title='Payment Type'
                                placeholder='Select'
                                controlWidth='10rem'
                                options={orderFilterData.payment_type}
                                isSearchable={false}
                                value={paymentType}
                                onChange={(data) => _handleSelectFilter(data, 4)} />
                        </>
                    }
                </div>

                <PrimaryButton name='Filter now'
                    paddingBlock='padding-12px-tb padding-36px-lr'
                    onClick={_handleFilter} />
            </div>

            {isTransactionView &&
                <div className='pt-1 padding-32px-lr d-flex gap-1 align-items-center justify-content-end'>
                    <div>
                        <img src={require("../../../Assets/Image/scroll-animation.gif")}
                            draggable={false}
                            className='e-rotate-270'
                            width={24}
                            height={24}
                            alt='scroll' />
                    </div>
                    <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-12px'>
                        Scroll horizontally for view more information
                    </p>
                </div>
            }

            {/* Order list table */}
            {loader === true || filterLoader === true ?
                <div className='d-flex align-items-center justify-content-center pt-4 min-vh-75'>
                    <img src={require("../../../Assets/Image/loader.gif")}
                        draggable={false}
                        alt='loader'
                        width={80}
                        height={80}
                        className='text-center' />
                </div>
                :
                userOrderData?.orders?.length > 0 ?
                    <div className={`${isTransactionView ? 'e-data-table-wrapper-default-updated' : 'e-data-table-wrapper-striped'} pt-3 padding-32px-bottom`}>
                        <CustomDataTable columns={tableColumns}
                            data={userOrderData.orders}
                            pagination={true}
                            paginationPerPage={10}
                            {...(!isTransactionView && {
                                expandableRowsComponent,
                                defaultExpandedRows: (row) => (row.product === "Goals" || row.product === "Basket") ? true : false,
                                striped: true,
                            })}
                            paginationDefaultPage={userOrderData.current_page}
                            paginationTotalRows={userOrderData.total_count}
                            lastPage={userOrderData.total_page}
                            onChangePage={_handleTablePageChange}
                        />
                    </div>
                    :
                    <EmptyScreen
                        image={require('../../../Assets/Image/NotFound/no-data.png')}
                        title='No results found'
                        description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                    />
            }

            {selectedTransaction && (selectedTab === 8 || selectedTab === 9) && (
                <SystematicPlanOrderModal data={selectedTransaction || {}}
                    type={selectedTab === 9 ? "STP" : "SWP"}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedTransaction(null);
                        }, 200);
                    }} />
            )}

            {selectedTransaction && selectedTab === 7 && (
                <OtherTransactionDetailModal data={selectedTransaction || {}}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedTransaction(null);
                        }, 200);
                    }} />
            )}
        </div>
    )
}

export default Order;
